import type { FormEvent, ReactNode } from 'react';

import LucideSymbolIcon from '../icons/LucideSymbolIcon';

export type ForumMessage = {
  id: string;
  contenu: string;
  auteur: string;
  dateCreation: string;
  estModerateur?: boolean;
  reponses?: Array<{
    id: string;
    contenu: string;
    auteur: string;
    dateCreation: string;
    estModerateur?: boolean;
  }>;
};

export function formatForumDate(value?: string) {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

function authorInitials(name: string) {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('') || '?'
  );
}

function ForumAuthorBadge({ name, date, estModerateur }: { name: string; date: string; estModerateur?: boolean }) {
  return (
    <header className="elearning-forum-message__head">
      <span className="elearning-forum-message__avatar" aria-hidden>
        {authorInitials(name)}
      </span>
      <div className="elearning-forum-message__meta min-w-0 flex-1">
        <div className="elearning-forum-message__author-row">
          <span className="elearning-forum-message__author">{name}</span>
          {estModerateur ? <span className="elearning-forum-message__badge">Formateur</span> : null}
        </div>
        <time className="elearning-forum-message__date" dateTime={date}>
          {formatForumDate(date)}
        </time>
      </div>
    </header>
  );
}

function ForumReply({ reply }: { reply: NonNullable<ForumMessage['reponses']>[number] }) {
  return (
    <article className="elearning-forum-reply">
      <ForumAuthorBadge name={reply.auteur} date={reply.dateCreation} estModerateur={reply.estModerateur} />
      <p className="elearning-forum-message__body">{reply.contenu}</p>
    </article>
  );
}

function ForumMessage({ message }: { message: ForumMessage }) {
  const replies = message.reponses ?? [];

  return (
    <article className="elearning-forum-message">
      <ForumAuthorBadge name={message.auteur} date={message.dateCreation} estModerateur={message.estModerateur} />
      <p className="elearning-forum-message__body">{message.contenu}</p>
      {replies.length > 0 ? (
        <div className="elearning-forum-replies" aria-label="Réponses">
          {replies.map((reply) => (
            <ForumReply key={reply.id} reply={reply} />
          ))}
        </div>
      ) : null}
    </article>
  );
}

export function ElearningForumCardShell({
  title,
  meta,
  description,
  children,
  footer,
}: {
  title: string;
  meta: string;
  description?: string | null;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <section className="elearning-forum-card" aria-label={title}>
      <div className="elearning-forum-card__accent" aria-hidden />
      <header className="elearning-forum-card__header">
        <span className="elearning-forum-card__icon" aria-hidden>
          <LucideSymbolIcon name="forum" tone="inherit" size={22} />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="elearning-forum-card__title">{title}</h2>
          <p className="elearning-forum-card__meta">{meta}</p>
          {description ? <p className="elearning-forum-card__description">{description}</p> : null}
        </div>
      </header>
      <div className="elearning-forum-card__body">{children}</div>
      {footer ? <footer className="elearning-forum-card__footer">{footer}</footer> : null}
    </section>
  );
}

export function ElearningForumMessageList({ messages }: { messages: ForumMessage[] }) {
  if (messages.length === 0) {
    return (
      <div className="elearning-forum-empty">
        <LucideSymbolIcon name="forum" tone="muted" size={28} />
        <p>Aucun message pour le moment. Lancez la discussion.</p>
      </div>
    );
  }

  return (
    <div className="elearning-forum-thread">
      {messages.map((entry) => (
        <ForumMessage key={entry.id} message={entry} />
      ))}
    </div>
  );
}

export function ElearningForumCompose({
  message,
  onMessageChange,
  onSubmit,
  fieldClass,
}: {
  message: string;
  onMessageChange: (value: string) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  fieldClass: string;
}) {
  return (
    <form className="elearning-forum-compose" onSubmit={onSubmit}>
      <label className="elearning-forum-compose__label" htmlFor="elearning-forum-message">
        Votre message
      </label>
      <textarea
        id="elearning-forum-message"
        className={`${fieldClass} elearning-forum-compose__input`}
        rows={4}
        placeholder="Partagez une question, une réflexion ou un retour…"
        value={message}
        onChange={(e) => onMessageChange(e.target.value)}
        required
      />
      <div className="elearning-forum-compose__actions">
        <button type="submit" className="apprenant-mes-cours-card__btn apprenant-mes-cours-card__btn--primary">
          <LucideSymbolIcon name="send" tone="inherit" size={16} />
          Publier
        </button>
      </div>
    </form>
  );
}
